/**
 * Federated sign-in (Google) through the Cognito Hosted UI — Authorization Code
 * + PKCE on the SAME public SPA app client the password login uses. There is no
 * client secret anywhere in this flow: PKCE is what stands in for it, so the
 * browser holds a single-use verifier instead of a credential.
 *
 * The pool issues the same token shapes SRP does, so the rest of the app keeps
 * reading the Cognito **ID token** as the API Bearer (see ./cognito.ts). This
 * module never imports the vendor SDK: the whole flow is one top-level
 * navigation plus form posts to the Hosted UI.
 *
 * Nothing here is logged: an OAuth callback URL carries the authorization code
 * in the query string and the token endpoint's error bodies can echo request
 * parameters, so both stay out of consoles and telemetry by construction.
 */

/** Session shape persisted between reloads. */
interface FederatedSession {
  idToken: string;
  refreshToken: string | null;
  /** Epoch ms when the ID token expires, read from its own `exp` claim. */
  expiresAt: number;
}

/** The in-flight OAuth transaction, consumed exactly once by the callback. */
interface PendingTransaction {
  verifier: string;
  state: string;
  returnPath: string;
}

/** Build-time configuration the Hosted UI flow needs. Exported for tests. */
export interface FederatedConfig {
  /** Hosted UI origin, e.g. "https://habilitaai-prod-auth.auth.us-east-1.amazoncognito.com". */
  origin: string;
  clientId: string;
  redirectUri: string;
}

export interface FederatedSignInResult {
  /** Where to send the person after the session is stored. Always an internal path. */
  returnPath: string;
}

/**
 * Durability on purpose: the Cognito SDK stores SRP sessions in localStorage
 * (its default Storage), so a password session already survives reloads and
 * browser restarts. The federated session lives at the SAME level — same XSS
 * exposure class, same behaviour — so neither login method feels different.
 */
const SESSION_KEY = 'habilita.federatedSession';

/**
 * sessionStorage, not localStorage: the transaction is single-use and scoped
 * to the tab that started it. A callback that cannot find it (another tab, a
 * replayed URL, a restarted browser) is answered with "recomece o login",
 * never with a guess.
 */
const PENDING_KEY = 'habilita.googleAuthPending';

const DEFAULT_RETURN_PATH = '/app';
/** Refresh the ID token when it is within this window of expiry. */
const REFRESH_SKEW_MS = 120 * 1000;
/** 32 random bytes → a 43-char base64url verifier, inside RFC 7636's 43–128. */
const RANDOM_BYTES = 32;

/** Reads build-time config; null when the Google button should not exist. */
function federatedConfig(): FederatedConfig | null {
  const domain = (import.meta.env.VITE_COGNITO_DOMAIN ?? '').trim().replace(/\/+$/, '');
  const clientId = (import.meta.env.VITE_COGNITO_CLIENT_ID ?? '').trim();
  if (!domain || !clientId) return null;
  // The Hosted UI is always https; a malformed value must fail closed, not
  // silently build an http:// or relative authorize URL.
  if (!domain.startsWith('https://')) return null;
  return {
    origin: domain,
    clientId,
    redirectUri: `${window.location.origin}/auth/callback`,
  };
}

/** True when the build has everything the "Entrar com Google" button needs. */
export function isGoogleSignInEnabled(): boolean {
  return federatedConfig() !== null;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(): string {
  const bytes = new Uint8Array(RANDOM_BYTES);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** RFC 7636 S256: BASE64URL(SHA-256(verifier)). */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Only internal paths may be returned to: anything that is not a plain
 * "/path" is dropped to the default. The `//` and `\` guards matter because
 * URL parsers read "//host" (and, in special schemes, "/\host") as
 * scheme-relative — i.e. an EXTERNAL origin — which would turn the callback's
 * final navigation into an open redirect.
 */
export function sanitizeReturnPath(candidate: string | null | undefined): string {
  if (
    !candidate ||
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes('\\')
  ) {
    return DEFAULT_RETURN_PATH;
  }
  return candidate;
}

/**
 * The Hosted UI authorize URL. `identity_provider=Google` skips the Hosted
 * UI's own account picker and lands straight on the Google consent screen.
 */
export function buildAuthorizeUrl(
  config: FederatedConfig,
  state: string,
  challenge: string,
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    identity_provider: 'Google',
  });
  return `${config.origin}/oauth2/authorize?${params.toString()}`;
}

/**
 * Starts the Google sign-in: stores the PKCE verifier + CSRF state as a
 * single-use pending transaction and navigates away to the Hosted UI. The
 * person's destination survives the OAuth round trip inside that transaction
 * (react-router location state would not).
 */
export async function startGoogleSignIn(returnPath?: string): Promise<void> {
  const config = federatedConfig();
  if (!config) {
    throw new Error('O login com Google não está disponível neste ambiente.');
  }
  const state = randomToken();
  const verifier = randomToken();
  const challenge = await pkceChallenge(verifier);
  const pending: PendingTransaction = {
    verifier,
    state,
    returnPath: sanitizeReturnPath(returnPath),
  };
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Storage disabled: the callback would find no transaction and fail
    // cleanly — but starting anyway would strand the person on an error page.
    throw new Error('Seu navegador bloqueou o armazenamento de sessão necessário ao login.');
  }
  window.location.assign(buildAuthorizeUrl(config, state, challenge));
}

/**
 * Completes the sign-in on /auth/callback: validates the CSRF state against
 * the pending transaction (consumed either way, so a replayed callback URL
 * cannot be retried), exchanges the authorization code at /oauth2/token —
 * public client: NO secret, the PKCE verifier is the proof — and stores the
 * session. Rejects with friendly pt-BR copy on every failure.
 */
export async function completeGoogleSignIn(
  code: string,
  state: string,
): Promise<FederatedSignInResult> {
  const config = federatedConfig();
  if (!config) {
    throw new Error('O login com Google não está disponível neste ambiente.');
  }

  const pending = readPending();
  clearPending();
  if (!pending || !code || pending.state !== state) {
    throw new Error('Não foi possível validar a resposta do Google. Recomece o login.');
  }

  const session = await requestTokens(config, {
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: pending.verifier,
  });
  storeSession(session);
  return { returnPath: sanitizeReturnPath(pending.returnPath) };
}

/**
 * A valid ID token for the stored federated session, refreshing it at
 * /oauth2/token when it is within ~2 min of expiry. Returns null when there is
 * no session (or it is unrecoverable) so the caller can fall back to the SRP
 * session — the two never coexist in practice, and whichever login completed
 * last is the one this prefers.
 */
export async function getFederatedIdToken(): Promise<string | null> {
  const session = loadSession();
  if (!session) return null;

  if (session.expiresAt - Date.now() > REFRESH_SKEW_MS) {
    return session.idToken;
  }

  const config = federatedConfig();
  if (!session.refreshToken || !config) {
    clearFederatedSession();
    return null;
  }

  try {
    const refreshed = await requestTokens(config, {
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: session.refreshToken,
    });
    storeSession({
      // Cognito's refresh grant issues no new refresh token — keep the current one.
      ...refreshed,
      refreshToken: refreshed.refreshToken ?? session.refreshToken,
    });
    return refreshed.idToken;
  } catch {
    clearFederatedSession();
    return null;
  }
}

/** Drops the stored federated session (paired with the SDK's own signOut). */
export function clearFederatedSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/** The error body is never read aloud: it can echo the code/tokens sent. */
async function requestTokens(
  config: FederatedConfig,
  grant: Record<string, string>,
): Promise<FederatedSession> {
  let response: Response;
  try {
    response = await fetch(`${config.origin}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(grant),
    });
  } catch {
    throw new Error(
      'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.',
    );
  }
  if (!response.ok) {
    throw new Error('Não foi possível concluir o login com Google. Tente novamente.');
  }

  const data = (await response.json()) as Record<string, unknown>;
  const idToken = typeof data.id_token === 'string' ? data.id_token : null;
  const expiresAt = idToken ? idTokenExpiryMs(idToken) : null;
  if (!idToken || !expiresAt) {
    throw new Error('A resposta do servidor de autenticação veio incompleta. Tente novamente.');
  }
  return {
    idToken,
    refreshToken: typeof data.refresh_token === 'string' ? data.refresh_token : null,
    expiresAt,
  };
}

/** Reads the `exp` claim (seconds → ms). No verification — the API verifies. */
function idTokenExpiryMs(jwt: string): number | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function readPending(): PendingTransaction | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingTransaction>;
    if (
      typeof parsed.verifier !== 'string' ||
      typeof parsed.state !== 'string' ||
      typeof parsed.returnPath !== 'string'
    ) {
      return null;
    }
    return parsed as PendingTransaction;
  } catch {
    return null;
  }
}

function clearPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function storeSession(session: FederatedSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage disabled: the session is still valid for THIS page load; it just
    // won't survive a reload (the person logs in again, as in a private window).
  }
}

function loadSession(): FederatedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FederatedSession>;
    if (typeof parsed.idToken !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    return {
      idToken: parsed.idToken,
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}
